import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import {
  IGendecRuleWire,
  IGendecSettingsWire,
  IGendecStationConfig,
  firstGendecConfigError,
  validateGendecConfig,
  validateStationConfigs,
} from "@vmosolutions/oc-interfaces";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { INotifications } from "src/API/AmplifyAPIWrapper";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { setGendecConfig, setGendecStale, updateGendecConfig } from "src/redux/actions/Gendec/Gendec.Actions";
import { GlobalSettings, IGendecGlobalSettings } from "./GlobalSettings";
import { IGendecRuleRow, RulesGrid, flightNumberToCell } from "./RulesGrid";
import { StationConfigsEditor } from "./StationConfigsEditor";

/**
 * Outer collapsible wrapper around the Per-Airport Recipients block. Default expanded
 * when there's nothing to hide yet (no airports or only a few configured), or when any
 * entry is currently invalid - so problems aren't tucked behind a closed accordion.
 * Beyond that the operator drives expansion: the local `useState` initializer runs
 * once at mount.
 */
const PerAirportRecipientsSection = (props: {
  configs: IGendecStationConfig[];
  onChange: (next: IGendecStationConfig[]) => void;
  flightTypes: React.ComponentProps<typeof StationConfigsEditor>["flightTypes"];
}): JSX.Element => {
  const { configs, onChange, flightTypes } = props;

  // Cheap re-derive each render; configs aren't usually huge and this drives the chip
  // counts shown in the summary. validateStationConfigs covers both per-entry issues
  // and the cross-entry invariants (one Default per airport, no duplicate flight-type
  // pairs) without re-implementing them here.
  const { airportCount, invalidCount } = useMemo(() => {
    const idents = new Set<string>();
    configs.forEach((c) => idents.add((c.airportIdent ?? "").toUpperCase()));
    const stationErrors = validateStationConfigs(configs);
    return { airportCount: idents.size, invalidCount: stationErrors.filter((e) => e !== undefined).length };
  }, [configs]);

  // Initial expand: open when there's something the operator should see (issues or a
  // small list); collapsed when there are lots of configured airports and nothing to
  // fix, to keep the GenDec tab compact. Runs once at mount.
  const [expanded, setExpanded] = useState<boolean>(() => invalidCount > 0 || airportCount <= 5);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, next) => setExpanded(next)}
      disableGutters
      sx={{ "&:before": { display: "none" }, mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">Per-Airport Recipients</Typography>
          <Chip size="small" label={`${airportCount} airport${airportCount === 1 ? "" : "s"}`} />
          {invalidCount > 0 && <Chip size="small" color="error" label={`${invalidCount} need attention`} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <StationConfigsEditor configs={configs} onChange={onChange} flightTypes={flightTypes} />
      </AccordionDetails>
    </Accordion>
  );
};

interface IManageGenDecProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  height: number;
}

const settingsFromConfig = (config: IGendecSettingsWire): IGendecGlobalSettings => ({
  enabled: config.enabled ?? false,
  sendTo: config.sendTo ?? [],
  cc: config.cc ?? [],
  replyTo: config.replyTo ?? "",
});

const rowsFromConfig = (config: IGendecSettingsWire): IGendecRuleRow[] =>
  (config.rules ?? []).map((rule) => ({
    ...rule,
    _rowId: crypto.randomUUID(),
    flightNumber: flightNumberToCell(rule.flightNumber),
  }));

const rowsToWireRules = (rows: IGendecRuleRow[]): IGendecRuleWire[] =>
  // Drop the synthetic row id; flightNumber stays a string (backend deserializes it).
  rows.map(({ _rowId, ...rule }) => rule);

/**
 * GenDec Management tab. Reads the current `ClientSettings.gendec` config
 * via the dedicated endpoints and lets ops edit the global settings + per-rule grid
 */
export const ManageGenDec = (props: IManageGenDecProps): JSX.Element => {
  const { height, setActiveModal } = props;
  const dispatch = useDispatch();
  const { cancellablePromise } = useCancellablePromise();
  const flightTypes = useSelector((state: IRootState) => state.structures?.customerStructure?.flightTypes ?? []);
  const stale = useSelector((state: IRootState) => state.gendec?.stale ?? false);
  // Gendec config snapshot from redux
  const snapshot = useSelector((state: IRootState) => state.gendec?.config ?? null);
  const initialRows = useMemo(() => (snapshot ? rowsFromConfig(snapshot) : []), [snapshot]);

  const [original, setOriginal] = useState<IGendecSettingsWire | null>(snapshot);
  const [settings, setSettings] = useState<IGendecGlobalSettings>(
    snapshot ? settingsFromConfig(snapshot) : { enabled: false, sendTo: [], cc: [], replyTo: "" }
  );
  const [rows, setRows] = useState<IGendecRuleRow[]>(initialRows);
  const [originalRows, setOriginalRows] = useState<IGendecRuleRow[]>(initialRows);
  const [stationConfigs, setStationConfigs] = useState<IGendecStationConfig[]>(snapshot?.stationConfigs ?? []);
  const [dirty, setDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const applyConfig = useCallback(
    (config: IGendecSettingsWire) => {
      const loaded = rowsFromConfig(config);
      setOriginal(config);
      setSettings(settingsFromConfig(config));
      setRows(loaded);
      setOriginalRows(loaded);
      setStationConfigs(config.stationConfigs ?? []);
      setDirty(false);
      // Any fresh snapshot supersedes the stale flag: whether we just fetched, saved,
      // or got a new initialConfig from AdminWindow, the local state is now authoritative.
      dispatch(setGendecStale(false));
    },
    [dispatch]
  );

  // Sync local state from the Redux snapshot whenever it changes AND the operator has
  // no unsaved edits. When dirty, don't auto-apply; the stale banner lets
  // the operator choose to discard their edits.
  useEffect(() => {
    if (snapshot && !dirty) applyConfig(snapshot);
  }, [snapshot, dirty, applyConfig]);

  const handleSettingsChange = useCallback((patch: Partial<IGendecGlobalSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const handleRowsChange = useCallback((next: IGendecRuleRow[]) => {
    setRows(next);
    setDirty(true);
  }, []);

  const handleStationConfigsChange = useCallback((next: IGendecStationConfig[]) => {
    setStationConfigs(next);
    setDirty(true);
  }, []);

  const handleDiscard = useCallback(() => {
    if (original) applyConfig(original);
  }, [original, applyConfig]);

  /**
   * Apply the Redux snapshot to local state, discarding the operator's unsaved edits
   */
  const handleReload = useCallback((): void => {
    if (snapshot) applyConfig(snapshot);
  }, [snapshot, applyConfig]);

  /** Reload guarded by a confirm dialog when the operator has unsaved edits. */
  const handleStaleReload = useCallback(() => {
    if (!dirty) {
      handleReload();
      return;
    }
    setActiveModal({
      title: "Reload GenDec configuration?",
      message: "Another operator updated this configuration. Reloading will discard your unsaved changes. Continue?",
      cancel: () => setActiveModal(null),
      confirm: () => {
        setActiveModal(null);
        handleReload();
      },
      confirmLabel: "Reload (discard my changes)",
    });
  }, [dirty, handleReload, setActiveModal]);

  // Live validation of the draft. Update is disabled when there are any errors - the
  // grid already paints invalid cells red so the user can see exactly which rows are
  // blocking the save.
  const validationError = useMemo(() => {
    const draftConfig: IGendecSettingsWire = {
      ...(original ?? {}),
      enabled: settings.enabled,
      sendTo: settings.sendTo,
      cc: settings.cc,
      replyTo: settings.replyTo,
      rules: rowsToWireRules(rows),
      stationConfigs,
    };
    return firstGendecConfigError(validateGendecConfig(draftConfig));
  }, [original, settings, rows, stationConfigs]);

  const handleSave = useCallback(async () => {
    // Update is disabled when invalid, but guard anyway in case it's invoked otherwise.
    if (validationError) {
      setActiveModal({
        title: "GenDec configuration invalid",
        message: validationError,
        cancel: () => setActiveModal(null),
        confirm: () => setActiveModal(null),
        confirmLabel: "OK",
      });
      return;
    }
    const config: IGendecSettingsWire = {
      ...(original ?? {}),
      enabled: settings.enabled,
      sendTo: settings.sendTo,
      cc: settings.cc,
      replyTo: settings.replyTo,
      rules: rowsToWireRules(rows),
      stationConfigs,
    };
    setIsSaving(true);
    const notifications: INotifications = {
      dispatch,
      infoMessage: "Saving GenDec configuration",
      successMessage: "GenDec configuration saved",
      errorMessage: "Error: unable to save GenDec configuration",
    };
    const saved = await updateGendecConfig(config, notifications, cancellablePromise);
    setIsSaving(false);
    if (saved) {
      applyConfig(saved);
      dispatch(setGendecConfig(saved));
    }
  }, [
    original,
    settings,
    rows,
    stationConfigs,
    dispatch,
    cancellablePromise,
    applyConfig,
    setActiveModal,
    validationError,
  ]);

  return (
    <CardContent sx={{ height: `calc(${height}px - 60px)`, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
        <Typography variant="h6">GenDec Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="warning" onClick={handleDiscard} disabled={!dirty || isSaving}>
            Reset
          </Button>
          <Tooltip title={validationError ?? ""} disableHoverListener={!validationError}>
            <span>
              <Button
                variant="contained"
                color="success"
                onClick={() => void handleSave()}
                disabled={!dirty || isSaving || !!validationError}
              >
                Update
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Hidden while saving so the WS echo from our own save doesn't flash the banner
          before the PUT response resolves and applyConfig clears stale + dirty. */}
      {stale && dirty && !isSaving && (
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            mb: 1,
            flexShrink: 0,
            bgcolor: "secondary.light",
            color: "rgba(0, 0, 0, 0.87)",
            "& .MuiAlert-icon": { color: "rgba(0, 0, 0, 0.87)" },
          }}
          action={
            <Button color="inherit" size="small" onClick={handleStaleReload}>
              Reload
            </Button>
          }
        >
          Another operator updated this configuration since you opened it. Reloading will discard your unsaved changes.
        </Alert>
      )}

      {/* Single scroll container so the header stays pinned and the three sections
          (global, per-airport, rules) share one scrollbar instead of fighting each
          other for the remaining card height. The RulesGrid still does its own
          virtualized scroll for the per-row case, but the outer scroll catches any
          overflow when the per-airport accordion stack grows tall. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
        <GlobalSettings settings={settings} onChange={handleSettingsChange} />

        <Divider sx={{ my: 2 }} />

        <PerAirportRecipientsSection
          configs={stationConfigs}
          onChange={handleStationConfigsChange}
          flightTypes={flightTypes}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Send Rules
        </Typography>
        <RulesGrid rows={rows} originalRows={originalRows} onChange={handleRowsChange} flightTypes={flightTypes} />
      </Box>
    </CardContent>
  );
};

export default ManageGenDec;
